import { DiscountCode, PaginatedDiscountCodes, PromoCodeSortColumn, Sort, ValidatedDiscountCode } from 'types';
import { toOrdering } from 'utils/ordering';
import HTTPClient, { HTTPClientParameters } from './HTTPClient';
import cache, { clearCachedMethod } from './cache';

type AddDiscountCodeInput = {
    code: string;
    spot: number;
    value: number | null;
    percentage_discount: string | null;
    applicable_rental_types: string[];
    minutes_to_discount: number | null;
    number_of_uses: string | null;
    expiration: Date | null;
    valid_from: Date | null;
    notes?: string;
    deleted?: boolean;
};

type GetDiscountCodesPayload = {
    spot: number;
    page: number;
    usableCodes: boolean;
    sort?: Sort<PromoCodeSortColumn> | null;
    search?: string;
};

type DiscountCodeClientParams = HTTPClientParameters;

class DiscountCodeClient extends HTTPClient {
    constructor(params: DiscountCodeClientParams) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    _getDiscountCodes({
        spot,
        page,
        usableCodes,
        sort,
        search,
    }: GetDiscountCodesPayload): Promise<PaginatedDiscountCodes> {
        const params = new URLSearchParams({
            spot: String(spot),
            page: String(page),
            usable_codes: String(usableCodes),
        });
        if (sort) params.set('ordering', toOrdering(sort));
        if (search) params.set('search', search);
        return this.get(`promo_codes/?${params.toString()}`);
    }

    @cache(5 * 60000) // 5min of cache
    getDiscountCodes(payload: GetDiscountCodesPayload): Promise<PaginatedDiscountCodes> {
        return this._getDiscountCodes(payload);
    }

    @cache(0)
    force__getDiscountCodes(payload: GetDiscountCodesPayload): Promise<PaginatedDiscountCodes> {
        return this._getDiscountCodes(payload);
    }

    // Drops every cached getDiscountCodes page, whatever its filters, sort or search, so the
    // next fetch after a code is added or deleted can't serve a stale list.
    clearDiscountCodesCache() {
        clearCachedMethod(this, 'getDiscountCodes');
    }

    addDiscountCode(promoCode: AddDiscountCodeInput) {
        return this.post(`promo_codes/`, promoCode);
    }

    deleteDiscountCode(promoCodePk: number) {
        return this.delete(`promo_codes/${promoCodePk}/`);
    }

    applyDiscountCode(slotPk: number, discountCode: string): Promise<DiscountCode> {
        return this.post('promo', {
            promo_code: discountCode,
            slotID: slotPk,
        });
    }

    generateDiscountQrCode(discountCodePk: number): Promise<ValidatedDiscountCode> {
        return this.post('generate_promo_qr_code/', {
            promo_code_pk: discountCodePk,
        });
    }
}

export default DiscountCodeClient;
